import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import { useFormDirty } from '../hooks/useFormDirty';
import { formatDate } from '../utils/date';

const INVOICE_STATUSES = ['draft', 'sent', 'paid', 'cancelled', 'overdue'];
const BILLING_MODELS = ['T&M', 'Fixed Price', 'Milestone', 'Subscription'];
const FREQUENCIES = ['monthly', 'quarterly', 'milestone', 'one-time'];
const GST_TYPES = [
  { value: 'intra_state', label: 'Intra-State (Maharashtra)' },
  { value: 'inter_state', label: 'Inter-State' },
  { value: 'union_territory', label: 'Union Territory' }
];
const TDS_RATES = [
  { value: '2', label: '2%' },
  { value: '10', label: '10%' }
];

const STATUS_BADGES = {
  draft: 'badge-gray', sent: 'badge-yellow', paid: 'badge-green',
  cancelled: 'badge-red', overdue: 'badge-red', active: 'badge-green', pending: 'badge-yellow'
};

export default function Billing() {
  const [tab, setTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [billingPlans, setBillingPlans] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterContract, setFilterContract] = useState('');

  const { dirty: invDirty, markDirty: markInvDirty, resetDirty: resetInvDirty } = useFormDirty();
  const { dirty: subDirty, markDirty: markSubDirty, resetDirty: resetSubDirty } = useFormDirty();

  // Invoice modal
  const [invModalOpen, setInvModalOpen] = useState(false);
  const [editingInv, setEditingInv] = useState(null);
  const [invForm, setInvForm] = useState({
    invoice_number: '', customer_id: '', contract_id: '', invoice_date: '',
    due_date: '', amount: '', tax: '', currency: 'INR', notes: '',
    gst_type: 'intra_state', basic_amount: '', tds_rate: '2',
    cgst_rate: 0, cgst_amount: 0, sgst_rate: 0, sgst_amount: 0,
    igst_rate: 0, igst_amount: 0, utgst_rate: 0, utgst_amount: 0,
    gross_amount: 0, tds_amount: 0, net_receivable: 0,
    customer_gstn: '', place_of_supply: '', sac_code: '998313'
  });
  const [invErrors, setInvErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Invoice detail
  const [selectedInv, setSelectedInv] = useState(null);
  const [lineItems, setLineItems] = useState([]);

  // Line item modal
  const [lineModal, setLineModal] = useState(false);
  const [lineForm, setLineForm] = useState({ description: '', quantity: '', unit: '', rate: '', amount: '' });
  const [editingLine, setEditingLine] = useState(null);

  // Billing plan modal
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planForm, setPlanForm] = useState({ contract_id: '', model: '', frequency: '', next_billing_date: '' });

  // Credit note modal
  const [cnModalOpen, setCnModalOpen] = useState(false);
  const [cnForm, setCnForm] = useState({ invoice_id: '', credit_note_number: '', amount: '', reason: '', date: '' });

  const fetchData = useCallback(async () => {
    try {
      let invoiceUrl = '/api/billing/invoices?';
      if (filterStatus) invoiceUrl += `status=${filterStatus}&`;
      if (filterContract) invoiceUrl += `contractId=${filterContract}&`;
      const [iRes, bRes, cnRes, cRes, ctRes] = await Promise.all([
        apiFetch(invoiceUrl),
        apiFetch('/api/billing/plans'),
        apiFetch('/api/billing/credit-notes'),
        apiFetch('/api/customers?limit=200'),
        apiFetch('/api/contracts?limit=500')
      ]);
      if (iRes.ok) { const d = await iRes.json(); setInvoices(d.invoices || []); }
      if (bRes.ok) { const d = await bRes.json(); setBillingPlans(d.billingPlans || []); }
      if (cnRes.ok) { const d = await cnRes.json(); setCreditNotes(d.creditNotes || []); }
      if (cRes.ok) { const d = await cRes.json(); setCustomers(d.customers || d.data || []); }
      if (ctRes.ok) { const d = await ctRes.json(); setContracts(d.contracts || []); }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, [filterStatus, filterContract]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmtCurrency = (v, cur) => v != null ? `${cur === 'INR' ? '\u20B9' : '$'}${parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '--';
  const fmtDate = (v) => formatDate(v, '--');

  // ---- Invoice CRUD ----
  function openCreateInvoice() {
    setEditingInv(null);
    setInvForm({
      invoice_number: '', customer_id: '', contract_id: '', invoice_date: '', due_date: '',
      amount: '', tax: '', currency: 'INR', notes: '',
      gst_type: 'intra_state', basic_amount: '', tds_rate: '2',
      cgst_rate: 0, cgst_amount: 0, sgst_rate: 0, sgst_amount: 0,
      igst_rate: 0, igst_amount: 0, utgst_rate: 0, utgst_amount: 0,
      gross_amount: 0, tds_amount: 0, net_receivable: 0,
      customer_gstn: '', place_of_supply: '', sac_code: '998313'
    });
    setInvErrors({}); resetInvDirty(); setInvModalOpen(true);
  }
  function openEditInvoice(inv) {
    setEditingInv(inv);
    setInvForm({
      invoice_number: inv.invoice_number || '', customer_id: inv.customer_id || '',
      contract_id: inv.contract_id || '',
      invoice_date: inv.invoice_date ? inv.invoice_date.substring(0, 10) : '',
      due_date: inv.due_date ? inv.due_date.substring(0, 10) : '',
      amount: inv.amount || '', tax: inv.tax || '', currency: inv.currency || 'INR', notes: inv.notes || '',
      gst_type: inv.gst_type || 'intra_state',
      basic_amount: inv.basic_amount || '',
      tds_rate: inv.tds_rate != null ? String(inv.tds_rate) : '2',
      cgst_rate: inv.cgst_rate || 0, cgst_amount: inv.cgst_amount || 0,
      sgst_rate: inv.sgst_rate || 0, sgst_amount: inv.sgst_amount || 0,
      igst_rate: inv.igst_rate || 0, igst_amount: inv.igst_amount || 0,
      utgst_rate: inv.utgst_rate || 0, utgst_amount: inv.utgst_amount || 0,
      gross_amount: inv.total || 0, tds_amount: inv.tds_amount || 0,
      net_receivable: inv.net_receivable || 0,
      customer_gstn: inv.customer_gstn || '', place_of_supply: inv.place_of_supply || '',
      sac_code: inv.sac_code || '998313'
    });
    setInvErrors({}); resetInvDirty(); setInvModalOpen(true);
  }

  async function recalculateGst(basicAmount, gstType, tdsRate) {
    if (!basicAmount || parseFloat(basicAmount) <= 0) {
      setInvForm(f => ({
        ...f, cgst_rate: 0, cgst_amount: 0, sgst_rate: 0, sgst_amount: 0,
        igst_rate: 0, igst_amount: 0, utgst_rate: 0, utgst_amount: 0,
        gross_amount: 0, tds_amount: 0, net_receivable: 0
      }));
      return;
    }
    try {
      const res = await apiFetch('/api/billing/invoices/calculate-gst', {
        method: 'POST',
        body: JSON.stringify({ basicAmount: parseFloat(basicAmount), gstType, tdsRate: parseFloat(tdsRate) })
      });
      if (res.ok) {
        const d = await res.json();
        setInvForm(f => ({
          ...f,
          cgst_rate: d.cgstRate, cgst_amount: d.cgstAmount,
          sgst_rate: d.sgstRate, sgst_amount: d.sgstAmount,
          igst_rate: d.igstRate, igst_amount: d.igstAmount,
          utgst_rate: d.utgstRate, utgst_amount: d.utgstAmount,
          gross_amount: d.grossAmount, tds_amount: d.tdsAmount,
          net_receivable: d.netReceivable,
          amount: parseFloat(basicAmount), tax: d.grossAmount - parseFloat(basicAmount),
          total: d.grossAmount
        }));
      }
    } catch (e) { /* handled */ }
  }

  function handleGstFieldChange(field, value) {
    markInvDirty();
    setInvForm(f => {
      const updated = { ...f, [field]: value };
      // Trigger recalculation when gst_type, basic_amount, or tds_rate changes
      if (field === 'gst_type' || field === 'basic_amount' || field === 'tds_rate') {
        const ba = field === 'basic_amount' ? value : updated.basic_amount;
        const gt = field === 'gst_type' ? value : updated.gst_type;
        const tr = field === 'tds_rate' ? value : updated.tds_rate;
        setTimeout(() => recalculateGst(ba, gt, tr), 0);
      }
      return updated;
    });
  }

  async function saveInvoice() {
    if (!invForm.invoice_number.trim()) { setInvErrors({ invoice_number: 'Invoice number required' }); return; }
    setSaving(true);
    const basic = parseFloat(invForm.basic_amount) || 0;
    const amt = parseFloat(invForm.amount) || basic;
    const tax = parseFloat(invForm.tax) || 0;
    const body = {
      invoiceNumber: invForm.invoice_number, customerId: invForm.customer_id || null,
      contractId: invForm.contract_id || null, invoiceDate: invForm.invoice_date || null,
      dueDate: invForm.due_date || null, amount: amt, tax: tax,
      total: invForm.gross_amount || (amt + tax),
      currency: invForm.currency, notes: invForm.notes || null,
      basicAmount: basic, gstType: invForm.gst_type,
      cgstRate: invForm.cgst_rate, cgstAmount: invForm.cgst_amount,
      sgstRate: invForm.sgst_rate, sgstAmount: invForm.sgst_amount,
      igstRate: invForm.igst_rate, igstAmount: invForm.igst_amount,
      utgstRate: invForm.utgst_rate, utgstAmount: invForm.utgst_amount,
      tdsRate: parseFloat(invForm.tds_rate) || 2, tdsAmount: invForm.tds_amount,
      netReceivable: invForm.net_receivable,
      customerGstn: invForm.customer_gstn || null,
      placeOfSupply: invForm.place_of_supply || null,
      sacCode: invForm.sac_code || '998313'
    };
    const url = editingInv ? `/api/billing/invoices/${editingInv.id}` : '/api/billing/invoices';
    try {
      const res = await apiFetch(url, { method: editingInv ? 'PUT' : 'POST', body: JSON.stringify(body) });
      if (res.ok) { resetInvDirty(); setInvModalOpen(false); fetchData(); }
      else { const d = await res.json(); setInvErrors({ invoice_number: d.error }); }
    } catch (e) { setInvErrors({ invoice_number: 'Failed to save' }); }
    setSaving(false);
  }

  async function sendInvoice(inv) {
    try {
      await apiFetch(`/api/billing/invoices/${inv.id}/send`, { method: 'POST' });
      fetchData();
    } catch (e) { /* handled */ }
  }

  async function cancelInvoice(inv) {
    if (!confirm('Cancel this invoice?')) return;
    try {
      await apiFetch(`/api/billing/invoices/${inv.id}/cancel`, { method: 'POST' });
      fetchData();
    } catch (e) { /* handled */ }
  }

  async function fetchInvoiceDetail(inv) {
    try {
      const res = await apiFetch(`/api/billing/invoices/${inv.id}`);
      if (res.ok) {
        const d = await res.json();
        setSelectedInv(d.invoice);
        setLineItems(d.lineItems || []);
      }
    } catch (e) { /* handled */ }
  }

  // ---- Line Items ----
  async function saveLine() {
    const qty = parseFloat(lineForm.quantity) || 0;
    const rate = parseFloat(lineForm.rate) || 0;
    const body = {
      description: lineForm.description, quantity: qty, unit: lineForm.unit || null,
      rate: rate, amount: lineForm.amount ? parseFloat(lineForm.amount) : (qty * rate)
    };
    const url = editingLine
      ? `/api/billing/invoices/${selectedInv.id}/line-items/${editingLine.id}`
      : `/api/billing/invoices/${selectedInv.id}/line-items`;
    try {
      await apiFetch(url, { method: editingLine ? 'PUT' : 'POST', body: JSON.stringify(body) });
      resetSubDirty(); setLineModal(false); fetchInvoiceDetail(selectedInv);
    } catch (e) { /* handled */ }
  }

  // ---- Billing Plan ----
  async function saveBillingPlan() {
    const body = {
      contractId: planForm.contract_id || null, model: planForm.model || null,
      frequency: planForm.frequency || null, nextBillingDate: planForm.next_billing_date || null
    };
    try {
      await apiFetch('/api/billing/plans', { method: 'POST', body: JSON.stringify(body) });
      resetSubDirty(); setPlanModalOpen(false); fetchData();
    } catch (e) { /* handled */ }
  }

  // ---- Credit Note ----
  async function saveCreditNote() {
    if (!cnForm.credit_note_number) return;
    const body = {
      invoiceId: cnForm.invoice_id || null, creditNoteNumber: cnForm.credit_note_number,
      amount: parseFloat(cnForm.amount) || 0, reason: cnForm.reason || null, date: cnForm.date || null
    };
    try {
      await apiFetch('/api/billing/credit-notes', { method: 'POST', body: JSON.stringify(body) });
      resetSubDirty(); setCnModalOpen(false); fetchData();
    } catch (e) { /* handled */ }
  }

  // ---- Invoice Detail View ----
  if (selectedInv) {
    return (
      <div>
        <button className="btn btn-secondary" style={{ marginBottom: 16 }} onClick={() => { setSelectedInv(null); setLineItems([]); }}>
          &larr; Back to Invoices
        </button>

        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0 }}>Invoice #{selectedInv.invoice_number}</h2>
              <span style={{ color: '#64748B' }}>{selectedInv.customer_name || 'N/A'}</span>
            </div>
            <span className={`badge ${STATUS_BADGES[selectedInv.status] || 'badge-gray'}`}>{selectedInv.status}</span>
          </div>
          <div style={{ display: 'flex', gap: 24, marginTop: 12, fontSize: 14, flexWrap: 'wrap' }}>
            <div><strong>Date:</strong> {fmtDate(selectedInv.invoice_date)}</div>
            <div><strong>Due:</strong> {fmtDate(selectedInv.due_date)}</div>
            <div><strong>Basic Amount:</strong> {fmtCurrency(selectedInv.basic_amount, selectedInv.currency)}</div>
            <div><strong>Total:</strong> <span style={{ fontWeight: 700 }}>{fmtCurrency(selectedInv.total, selectedInv.currency)}</span></div>
          </div>
          {selectedInv.basic_amount > 0 && (
            <div style={{ marginTop: 12, padding: 12, background: '#F8FAFC', borderRadius: 8, fontSize: 13 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>GST Breakdown ({GST_TYPES.find(g => g.value === selectedInv.gst_type)?.label || selectedInv.gst_type})</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                <div><strong>Basic Amount:</strong> {fmtCurrency(selectedInv.basic_amount, selectedInv.currency)}</div>
                {selectedInv.gst_type === 'intra_state' && (
                  <>
                    <div><strong>CGST @{selectedInv.cgst_rate}%:</strong> {fmtCurrency(selectedInv.cgst_amount, selectedInv.currency)}</div>
                    <div><strong>SGST @{selectedInv.sgst_rate}%:</strong> {fmtCurrency(selectedInv.sgst_amount, selectedInv.currency)}</div>
                  </>
                )}
                {selectedInv.gst_type === 'inter_state' && (
                  <div><strong>IGST @{selectedInv.igst_rate}%:</strong> {fmtCurrency(selectedInv.igst_amount, selectedInv.currency)}</div>
                )}
                {selectedInv.gst_type === 'union_territory' && (
                  <div><strong>UTGST @{selectedInv.utgst_rate}%:</strong> {fmtCurrency(selectedInv.utgst_amount, selectedInv.currency)}</div>
                )}
                <div><strong>Gross Amount:</strong> {fmtCurrency(selectedInv.total, selectedInv.currency)}</div>
                <div><strong>TDS @{selectedInv.tds_rate}%:</strong> -{fmtCurrency(selectedInv.tds_amount, selectedInv.currency)}</div>
                <div><strong>Net Receivable:</strong> <span style={{ fontWeight: 700, color: '#059669' }}>{fmtCurrency(selectedInv.net_receivable, selectedInv.currency)}</span></div>
              </div>
              {selectedInv.customer_gstn && <div style={{ marginTop: 6 }}><strong>Customer GSTN:</strong> {selectedInv.customer_gstn}</div>}
              {selectedInv.place_of_supply && <div><strong>Place of Supply:</strong> {selectedInv.place_of_supply}</div>}
              {selectedInv.sac_code && <div><strong>SAC Code:</strong> {selectedInv.sac_code}</div>}
            </div>
          )}
          {selectedInv.notes && <div style={{ marginTop: 8, fontSize: 14, color: '#64748B' }}>{selectedInv.notes}</div>}
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            {selectedInv.status === 'draft' && (
              <>
                <button className="btn btn-primary btn-sm" onClick={() => sendInvoice(selectedInv)}>Send Invoice</button>
                <button className="btn btn-secondary btn-sm" onClick={() => openEditInvoice(selectedInv)}>Edit</button>
              </>
            )}
            {selectedInv.status !== 'cancelled' && selectedInv.status !== 'paid' && (
              <button className="btn btn-danger btn-sm" onClick={() => cancelInvoice(selectedInv)}>Cancel</button>
            )}
          </div>
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3>Line Items</h3>
            <button className="btn btn-primary btn-sm" onClick={() => { setEditingLine(null); setLineForm({ description: '', quantity: '', unit: '', rate: '', amount: '' }); resetSubDirty(); setLineModal(true); }}>
              + Add Line Item
            </button>
          </div>
          <table className="data-table">
            <thead><tr><th>Description</th><th>Qty</th><th>Unit</th><th>Rate</th><th>Amount</th><th>Actions</th></tr></thead>
            <tbody>
              {lineItems.map(li => (
                <tr key={li.id}>
                  <td>{li.description || '--'}</td>
                  <td>{li.quantity}</td>
                  <td>{li.unit || '--'}</td>
                  <td>{fmtCurrency(li.rate)}</td>
                  <td>{fmtCurrency(li.amount)}</td>
                  <td>
                    <button className="btn btn-secondary btn-xs" onClick={() => { setEditingLine(li); setLineForm({ description: li.description || '', quantity: li.quantity || '', unit: li.unit || '', rate: li.rate || '', amount: li.amount || '' }); resetSubDirty(); setLineModal(true); }}>Edit</button>
                  </td>
                </tr>
              ))}
              {lineItems.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94A3B8' }}>No line items</td></tr>}
            </tbody>
          </table>
        </div>

        {lineModal && (
          <Modal title={editingLine ? 'Edit Line Item' : 'Add Line Item'} onClose={() => setLineModal(false)} dirty={subDirty}>
            <FormField label="Description" type="textarea" value={lineForm.description} onChange={v => { markSubDirty(); setLineForm(f => ({ ...f, description: v })); }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label="Quantity" type="number" value={lineForm.quantity} onChange={v => { markSubDirty(); setLineForm(f => ({ ...f, quantity: v })); }} />
              <FormField label="Unit" value={lineForm.unit} onChange={v => { markSubDirty(); setLineForm(f => ({ ...f, unit: v })); }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label="Rate" type="number" value={lineForm.rate} onChange={v => { markSubDirty(); setLineForm(f => ({ ...f, rate: v })); }} />
              <FormField label="Amount" type="number" value={lineForm.amount} onChange={v => { markSubDirty(); setLineForm(f => ({ ...f, amount: v })); }} />
            </div>
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setLineModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveLine}>Save</button>
            </div>
          </Modal>
        )}
      </div>
    );
  }

  // ---- Main List View ----
  return (
    <div>
      <div className="tab-bar" style={{ marginBottom: 16 }}>
        <button className={`tab-btn${tab === 'invoices' ? ' active' : ''}`} onClick={() => setTab('invoices')}>Invoices</button>
        <button className={`tab-btn${tab === 'plans' ? ' active' : ''}`} onClick={() => setTab('plans')}>Billing Plans</button>
        <button className={`tab-btn${tab === 'credit-notes' ? ' active' : ''}`} onClick={() => setTab('credit-notes')}>Credit Notes</button>
      </div>

      {tab === 'invoices' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <select className="form-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">All Statuses</option>
                {INVOICE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select className="form-select" value={filterContract} onChange={e => setFilterContract(e.target.value)}>
                <option value="">All Contracts</option>
                {contracts.map(c => <option key={c.id} value={c.id}>{c.contract_number}</option>)}
              </select>
            </div>
            <button className="btn btn-primary" onClick={openCreateInvoice}>+ New Invoice</button>
          </div>
          {loading ? <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div> : (
            <div className="card">
              <table className="data-table">
                <thead><tr><th>Invoice #</th><th>Customer</th><th>Contract</th><th>Date</th><th>Due</th><th>Total</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id} style={{ cursor: 'pointer' }} onClick={() => fetchInvoiceDetail(inv)}>
                      <td><strong>{inv.invoice_number}</strong></td>
                      <td>{inv.customer_name || '--'}</td>
                      <td>{inv.contract_number || '--'}</td>
                      <td>{fmtDate(inv.invoice_date)}</td>
                      <td>{fmtDate(inv.due_date)}</td>
                      <td>{fmtCurrency(inv.total)}</td>
                      <td><span className={`badge ${STATUS_BADGES[inv.status] || 'badge-gray'}`}>{inv.status}</span></td>
                      <td>
                        {inv.status === 'draft' && (
                          <button className="btn btn-primary btn-xs" onClick={e => { e.stopPropagation(); sendInvoice(inv); }}>Send</button>
                        )}
                        <button className="btn btn-secondary btn-xs" style={{ marginLeft: 4 }} onClick={e => { e.stopPropagation(); openEditInvoice(inv); }}>Edit</button>
                      </td>
                    </tr>
                  ))}
                  {invoices.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', color: '#94A3B8', padding: 40 }}>No invoices found</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'plans' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={() => { setPlanForm({ contract_id: '', model: '', frequency: '', next_billing_date: '' }); resetSubDirty(); setPlanModalOpen(true); }}>+ New Plan</button>
          </div>
          <div className="card">
            <table className="data-table">
              <thead><tr><th>Contract</th><th>Model</th><th>Frequency</th><th>Next Billing</th><th>Status</th></tr></thead>
              <tbody>
                {billingPlans.map(bp => (
                  <tr key={bp.id}>
                    <td>{bp.contract_number || bp.contract_id || '--'}</td>
                    <td>{bp.model || '--'}</td>
                    <td>{bp.frequency || '--'}</td>
                    <td>{fmtDate(bp.next_billing_date)}</td>
                    <td><span className={`badge ${STATUS_BADGES[bp.status] || 'badge-gray'}`}>{bp.status}</span></td>
                  </tr>
                ))}
                {billingPlans.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94A3B8', padding: 40 }}>No billing plans</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'credit-notes' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={() => { setCnForm({ invoice_id: '', credit_note_number: '', amount: '', reason: '', date: '' }); resetSubDirty(); setCnModalOpen(true); }}>+ New Credit Note</button>
          </div>
          <div className="card">
            <table className="data-table">
              <thead><tr><th>CN #</th><th>Invoice</th><th>Amount</th><th>Reason</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                {creditNotes.map(cn => (
                  <tr key={cn.id}>
                    <td><strong>{cn.credit_note_number}</strong></td>
                    <td>{cn.invoice_number || '--'}</td>
                    <td>{fmtCurrency(cn.amount)}</td>
                    <td>{cn.reason || '--'}</td>
                    <td><span className={`badge ${STATUS_BADGES[cn.status] || 'badge-gray'}`}>{cn.status}</span></td>
                    <td>{fmtDate(cn.date)}</td>
                  </tr>
                ))}
                {creditNotes.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94A3B8', padding: 40 }}>No credit notes</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Invoice Modal */}
      {invModalOpen && (
        <Modal title={editingInv ? 'Edit Invoice' : 'New Invoice'} onClose={() => setInvModalOpen(false)} dirty={invDirty}>
          <FormField label="Invoice Number" value={invForm.invoice_number} onChange={v => { markInvDirty(); setInvForm(f => ({ ...f, invoice_number: v })); }} error={invErrors.invoice_number} required />
          <FormField label="Customer" type="select" options={customers.map(c => ({ value: c.id, label: c.name }))} value={invForm.customer_id} onChange={v => { markInvDirty(); setInvForm(f => ({ ...f, customer_id: v })); }} />
          <FormField label="Contract" type="select" options={contracts.map(c => ({ value: c.id, label: c.contract_number }))} value={invForm.contract_id} onChange={v => { markInvDirty(); setInvForm(f => ({ ...f, contract_id: v })); }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Invoice Date" type="date" value={invForm.invoice_date} onChange={v => { markInvDirty(); setInvForm(f => ({ ...f, invoice_date: v })); }} />
            <FormField label="Due Date" type="date" value={invForm.due_date} onChange={v => { markInvDirty(); setInvForm(f => ({ ...f, due_date: v })); }} />
          </div>

          {/* GST Section */}
          <div style={{ marginTop: 16, padding: 12, border: '1px solid #E2E8F0', borderRadius: 8, background: '#F8FAFC' }}>
            <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>GST Details</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <FormField label="GST Type" type="select" options={GST_TYPES} value={invForm.gst_type} onChange={v => handleGstFieldChange('gst_type', v)} />
              <FormField label="Basic Amount" type="number" value={invForm.basic_amount} onChange={v => handleGstFieldChange('basic_amount', v)} />
              <FormField label="TDS Rate" type="select" options={TDS_RATES} value={invForm.tds_rate} onChange={v => handleGstFieldChange('tds_rate', v)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label="Customer GSTN" value={invForm.customer_gstn} onChange={v => { markInvDirty(); setInvForm(f => ({ ...f, customer_gstn: v })); }} />
              <FormField label="Place of Supply" value={invForm.place_of_supply} onChange={v => { markInvDirty(); setInvForm(f => ({ ...f, place_of_supply: v })); }} />
            </div>

            {/* Auto-calculated GST Breakdown */}
            {parseFloat(invForm.basic_amount) > 0 && (
              <div style={{ marginTop: 12, padding: 10, background: '#fff', borderRadius: 6, border: '1px solid #E2E8F0', fontSize: 13 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Calculated Breakdown</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <div>Basic Amount: <strong>{fmtCurrency(invForm.basic_amount, 'INR')}</strong></div>
                  {invForm.gst_type === 'intra_state' && (
                    <>
                      <div>CGST @{invForm.cgst_rate}%: <strong>{fmtCurrency(invForm.cgst_amount, 'INR')}</strong></div>
                      <div>SGST @{invForm.sgst_rate}%: <strong>{fmtCurrency(invForm.sgst_amount, 'INR')}</strong></div>
                    </>
                  )}
                  {invForm.gst_type === 'inter_state' && (
                    <div>IGST @{invForm.igst_rate}%: <strong>{fmtCurrency(invForm.igst_amount, 'INR')}</strong></div>
                  )}
                  {invForm.gst_type === 'union_territory' && (
                    <div>UTGST @{invForm.utgst_rate}%: <strong>{fmtCurrency(invForm.utgst_amount, 'INR')}</strong></div>
                  )}
                  <div>Gross Amount: <strong>{fmtCurrency(invForm.gross_amount, 'INR')}</strong></div>
                  <div>TDS @{invForm.tds_rate}%: <strong style={{ color: '#DC2626' }}>-{fmtCurrency(invForm.tds_amount, 'INR')}</strong></div>
                  <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #E2E8F0', paddingTop: 6, marginTop: 4 }}>
                    Net Receivable: <strong style={{ color: '#059669', fontSize: 15 }}>{fmtCurrency(invForm.net_receivable, 'INR')}</strong>
                  </div>
                </div>
              </div>
            )}
          </div>

          <FormField label="Notes" type="textarea" value={invForm.notes} onChange={v => { markInvDirty(); setInvForm(f => ({ ...f, notes: v })); }} />
          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setInvModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveInvoice} disabled={saving}>{saving ? 'Saving...' : (editingInv ? 'Update' : 'Create')}</button>
          </div>
        </Modal>
      )}

      {/* Billing Plan Modal */}
      {planModalOpen && (
        <Modal title="New Billing Plan" onClose={() => setPlanModalOpen(false)} dirty={subDirty}>
          <FormField label="Contract" type="select" options={contracts.map(c => ({ value: c.id, label: c.contract_number }))} value={planForm.contract_id} onChange={v => { markSubDirty(); setPlanForm(f => ({ ...f, contract_id: v })); }} />
          <FormField label="Model" type="select" options={BILLING_MODELS.map(m => ({ value: m, label: m }))} value={planForm.model} onChange={v => { markSubDirty(); setPlanForm(f => ({ ...f, model: v })); }} />
          <FormField label="Frequency" type="select" options={FREQUENCIES.map(f => ({ value: f, label: f }))} value={planForm.frequency} onChange={v => { markSubDirty(); setPlanForm(f => ({ ...f, frequency: v })); }} />
          <FormField label="Next Billing Date" type="date" value={planForm.next_billing_date} onChange={v => { markSubDirty(); setPlanForm(f => ({ ...f, next_billing_date: v })); }} />
          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setPlanModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveBillingPlan}>Create</button>
          </div>
        </Modal>
      )}

      {/* Credit Note Modal */}
      {cnModalOpen && (
        <Modal title="New Credit Note" onClose={() => setCnModalOpen(false)} dirty={subDirty}>
          <FormField label="Credit Note Number" value={cnForm.credit_note_number} onChange={v => { markSubDirty(); setCnForm(f => ({ ...f, credit_note_number: v })); }} required />
          <FormField label="Invoice" type="select" options={invoices.map(i => ({ value: i.id, label: i.invoice_number }))} value={cnForm.invoice_id} onChange={v => { markSubDirty(); setCnForm(f => ({ ...f, invoice_id: v })); }} />
          <FormField label="Amount" type="number" value={cnForm.amount} onChange={v => { markSubDirty(); setCnForm(f => ({ ...f, amount: v })); }} />
          <FormField label="Reason" type="textarea" value={cnForm.reason} onChange={v => { markSubDirty(); setCnForm(f => ({ ...f, reason: v })); }} />
          <FormField label="Date" type="date" value={cnForm.date} onChange={v => { markSubDirty(); setCnForm(f => ({ ...f, date: v })); }} />
          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setCnModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveCreditNote}>Create</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
